import { biApi } from '@/lib/api'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export async function registerServiceWorker(): Promise<void> {
  if (!isPushSupported() || window.location.protocol !== 'https:') return
  try {
    await navigator.serviceWorker.register('/sw.js')
  } catch (err) {
    console.error('Service worker registration failed:', err)
  }
}

export async function subscribeToPushNotifications(): Promise<boolean> {
  if (!isPushSupported()) return false

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return false

    const registration = await navigator.serviceWorker.ready
    const vapidResponse = await biApi.getVapidPublicKey()
    const vapidPublicKey = vapidResponse.data?.data?.vapidPublicKey
    if (!vapidPublicKey) return false

    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource,
      })
    }

    const json = subscription.toJSON()
    await biApi.subscribeToPush({
      endpoint: json.endpoint as string,
      keys: {
        p256dh: (json.keys as { p256dh: string }).p256dh,
        auth: (json.keys as { auth: string }).auth,
      },
    })

    return true
  } catch (err) {
    console.error('Push subscription failed:', err)
    return false
  }
}

export async function unsubscribeFromPushNotifications(): Promise<void> {
  if (!isPushSupported()) return
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await biApi.unsubscribeFromPush(subscription.endpoint)
      await subscription.unsubscribe()
    }
  } catch (err) {
    console.error('Push unsubscription failed:', err)
  }
}

export async function getPushStatus(): Promise<{
  supported: boolean
  permission: NotificationPermission
  subscribed: boolean
}> {
  if (!isPushSupported()) {
    return { supported: false, permission: 'denied', subscribed: false }
  }
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!subscription,
  }
}
